import { useCallback, useMemo, useState } from 'react'
import { useQuerySearchState } from 'src/hooks/useQuerySearchState'

export type SidebarMenuItem = {
  name: string
  label?: string
  group: string
  icon?: string
  // type defaults to 'menu' (tab). When 'button', the item is rendered as an action button in the sidebar
  type?: 'menu' | 'button'
  // action is only used when type === 'button'
  action?: () => void
}

export type SidebarGroup = {
  group: string
  items: SidebarMenuItem[]
}

export type SidebarConfig = {
  title: string
  groups: SidebarGroup[]
  // active tab is tracked by unique name
  active: string
  // accessibility ids map name->ids (only for tab items)
  a11y: Record<string, { tabId: string; panelId: string }>
  // expose click handler on config for convenience (for tab items)
  onClick: (name: string) => void
}

export type UseSidebarArgs = {
  title: string
  menus: SidebarMenuItem[]
  initialActiveName?: string
}

export function useSidebar({ title, menus, initialActiveName }: UseSidebarArgs) {
  // tab items are items that are not explicit 'button'
  const tabMenus = useMemo(() => menus.filter(m => m.type !== 'button'), [menus])
  const initialActiveNameBasedOnInitial = useMemo(() => {
    return initialActiveName && tabMenus.some(m => m.name === initialActiveName)
      ? initialActiveName
      : tabMenus[0]?.name ?? ''
  }, [initialActiveName, tabMenus])

  const [active, setActive] = useQuerySearchState('tab', initialActiveNameBasedOnInitial, {
    removeIfInitial: false
  })

  // create groups in stable order of appearance (include both tabs and buttons)
  const groups: SidebarGroup[] = useMemo(() => {
    const order: string[] = []
    const map = new Map<string, SidebarMenuItem[]>()
    menus.forEach(m => {
      if (!map.has(m.group)) {
        map.set(m.group, [])
        order.push(m.group)
      }
      const arr = map.get(m.group)
      if (arr) arr.push(m)
    })
    return order.map(g => ({ group: g, items: map.get(g) || [] }))
  }, [menus])

  // flat list for keyboard navigation (tabs only)
  const flatNames = useMemo(() => tabMenus.map(m => m.name), [tabMenus])

  const onClick = useCallback(
    (name: string) => {
      if (name && name !== active) setActive(name)
    },
    [active, setActive]
  )

  const a11y = useMemo(() => {
    const ids: Record<string, { tabId: string; panelId: string }> = {}
    flatNames.forEach((name, idx) => {
      const safe = name.toLowerCase().replace(/[^a-z0-9]+/g, '-')
      ids[name] = {
        tabId: `tp-sidebar-tab-${safe}-${idx}`,
        panelId: `tp-sidebar-panel-${safe}-${idx}`
      }
    })
    return ids
  }, [flatNames])

  const config: SidebarConfig = useMemo(() => ({ title, groups, active, a11y, onClick }), [
    title,
    groups,
    active,
    a11y,
    onClick
  ])

  const api = {
    config,
    onClick,
    getActive: () => active,
    setActive
  }

  return api
}
export default useSidebar
